import { useMemo, useState } from 'react';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import { usePlanetView } from '../contexts/PlanetViewContext';
import { usePlanetStore } from '../store/planetStore';
import { formatResourceText } from '../utils/resourceQuantities';
import { cancelBuildDialogContent } from '../utils/cancelBuildDialog';
import { destroyBuilding, type PlanetActionOptions } from '../services/planet.service';
import {
    BUILDING_DEFINITIONS,
    getQueueLimit,
    type BuildingType,
    type Planet,
    type ResourceCost,
    type TickCheckpointEventOf,
} from '@kaeliux/shared';
import {
    calculateBuildingUpgradeCost,
    calculateNominalRefundAtRates,
    canAffordCost,
    canUpgradeBuilding,
    extractBuildingLevels,
    getUnmetRequirements,
    planBuildingQueueMax,
    REFUND_RATES,
    type QueueMaxLimit,
    type UnmetRequirement,
} from '@kaeliux/game-engine';

/**
 * The Platinum "Queue max" action for this building (issue #219): how many
 * levels one click would queue right now, their summed cost and what stops
 * it there. `visible` is false when the view hides it, nothing beyond a
 * single level fits, or the viewer can't manage the planet.
 */
export interface QueueMaxPreview {
    access: 'granted' | 'locked' | 'hidden';
    visible: boolean;
    count: number;
    totalCost: ResourceCost;
    limitedBy: QueueMaxLimit;
}

export interface UsePlanetBuildActionsArgs {
    planet: Planet;
    buildingType: BuildingType;
    scienceLevels: Partial<Record<string, number>>;
    projectedLevel: number;
    queuedEvents: TickCheckpointEventOf<'buildingBuild'>[];
    queueFull: boolean;
    options: PlanetActionOptions;
    /**
     * The plot this building is being constructed on (DS-28) — only sent to
     * the server, and only meaningful, for a level 0→1 construct; ignored
     * for an upgrade of an already-built building, which already has one.
     */
    plotId?: string;
    /** Called after a successful upgrade/destroy, once the store has the fresh planet. */
    onUpgraded?: () => void;
    /** Called after a successful "Queue max", with how many levels were queued. */
    onQueuedMax?: (count: number) => void;
    /** Called after a queued level (and anything above it) was cancelled. */
    onCancelled?: () => void;
    onDestroyed?: () => void;
}

export interface UsePlanetBuildActionsResult {
    def: (typeof BUILDING_DEFINITIONS)[BuildingType];
    level: number;
    nextCost: ResourceCost;
    atMax: boolean;
    canPrereq: boolean;
    unmetRequirements: UnmetRequirement[];
    affordable: boolean;
    energyShortfall: boolean;
    /** True when the primary Upgrade action must be disabled outright (not just discouraged). */
    disabled: boolean;
    /** Human-readable reason the action is unavailable, or null when it isn't. */
    disabledReason: string | null;
    isRequesting: boolean;
    error: string | null;
    handleUpgrade: () => Promise<void>;
    queueMax: QueueMaxPreview;
    isQueueingMax: boolean;
    handleQueueMax: () => Promise<void>;
    /** Queue full and a higher premium tier would add slots — show "Unlock more slots". */
    showUnlockSlots: boolean;
    /**
     * The queued level a cancel would remove: the construct itself for a
     * not-yet-built building, otherwise its highest queued level (so higher
     * levels never outlive the one they depend on). Null when nothing is queued.
     */
    cancelTarget: TickCheckpointEventOf<'buildingBuild'> | null;
    isCancelling: boolean;
    requestCancel: (now: number) => void;
    destroyRefund: ResourceCost | null;
    destroyOverflow: number;
    isDestroying: boolean;
    destroyError: string | null;
    requestDestroy: () => void;
}

/**
 * Cost/prerequisite/queue-limit/destroy-confirm/error-surfacing logic shared
 * by every place a single building's upgrade/destroy action is offered
 * (`ZonePage`'s `BuildingRow` and DS-17's `BuildingPopover`). Extracted so
 * both call sites go through the exact same checks and the exact same
 * mutations rather than carrying independent copies that can drift (see
 * issue #187's implementer notes). The upgrade mutation and the
 * manage/destroy permissions come from `usePlanetView`, so the same hook
 * drives a personal planet and the alliance Team planet.
 */
export function usePlanetBuildActions({
    planet,
    buildingType,
    scienceLevels,
    projectedLevel,
    queuedEvents,
    queueFull,
    options,
    plotId,
    onUpgraded,
    onQueuedMax,
    onCancelled,
    onDestroyed,
}: UsePlanetBuildActionsArgs): UsePlanetBuildActionsResult {
    const { openDialog } = useConfirmDialog();
    const view = usePlanetView();
    const [isRequesting, setIsRequesting] = useState(false);
    const [isQueueingMax, setIsQueueingMax] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isDestroying, setIsDestroying] = useState(false);
    const [destroyError, setDestroyError] = useState<string | null>(null);

    const def = BUILDING_DEFINITIONS[buildingType];
    const state = planet.buildings[buildingType];
    const level = state?.level ?? 0;
    const nextCost = state?.nextCost ?? def.baseCost;
    const buildingLevels = useMemo(() => extractBuildingLevels(planet.buildings), [planet.buildings]);

    const atMax = projectedLevel >= def.maxLevel;
    const canPrereq = canUpgradeBuilding(buildingType, level, buildingLevels, scienceLevels as Partial<Record<never, number>>);
    const unmetRequirements = getUnmetRequirements(def.requirements, buildingLevels, scienceLevels as Partial<Record<never, number>>);
    const energyAvailable = planet.resources.energy.rate - (planet.resources.energy.consumed ?? 0);
    const affordable = canAffordCost(nextCost, {
        metal: planet.resources.metal.current,
        minerals: planet.resources.minerals.current,
        gas: planet.resources.gas.current,
        energy: energyAvailable,
        ozone: planet.state.ozone.current,
    });
    const energyShortfall = !!nextCost.energy && energyAvailable < nextCost.energy;
    const disabled = !view.canManage || atMax || !canPrereq || queueFull || !affordable || isRequesting || isQueueingMax || isDestroying || isCancelling;

    // Same engine function the server runs, against the planet as the client
    // sees it, with energy at `rate − consumed` like the single-level check.
    // A locked (upsell) preview uses Platinum's queue limit, so it shows what
    // the perk would actually queue.
    const previewQueueLimit = view.queueMaxAccess === 'locked' ? getQueueLimit('platinum') : view.buildingQueueLimit;
    const queueMaxPlan = useMemo(() => {
        if (view.queueMaxAccess === 'hidden') return null;
        const previewPlanet: Planet = {
            ...planet,
            resources: { ...planet.resources, energy: { ...planet.resources.energy, current: energyAvailable } },
        };
        return planBuildingQueueMax(
            previewPlanet,
            planet.tickCheckpoints ?? [],
            buildingType,
            scienceLevels,
            new Date(),
            options.speedMultiplier ?? 1,
            previewQueueLimit,
            planet.nextEventId ?? 1,
            level === 0 ? plotId : undefined,
        );
    }, [view.queueMaxAccess, previewQueueLimit, planet, energyAvailable, buildingType, scienceLevels, options.speedMultiplier, level, plotId]);

    const queueMax: QueueMaxPreview = {
        access: view.queueMaxAccess,
        visible: !!queueMaxPlan && view.canManage && queueMaxPlan.count > 1,
        count: queueMaxPlan?.count ?? 0,
        totalCost: queueMaxPlan?.totalCost ?? {},
        limitedBy: queueMaxPlan?.limitedBy ?? 'queue',
    };
    const showUnlockSlots = queueFull && view.canManage && view.canUnlockMoreSlots;

    const disabledReason: string | null = !view.canManage
        ? view.readOnlyReason
        : atMax
            ? 'Max level'
            : !canPrereq
                ? null // shown as the full-width UnmetRequirementsGrid by the caller instead
                : queueFull
                    ? 'Queue full'
                    : energyShortfall
                        ? 'Insufficient energy'
                        : !affordable
                            ? 'Insufficient resources'
                            : null;

    async function handleUpgrade() {
        setIsRequesting(true);
        setError(null);
        try {
            await view.upgradeBuilding(planet, buildingType, options, level === 0 ? plotId : undefined);
            onUpgraded?.();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Upgrade failed');
        } finally {
            setIsRequesting(false);
        }
    }

    async function handleQueueMax() {
        if (queueMax.access !== 'granted' || !view.queueMaxBuildingLevels || isDestroying) return;
        setIsQueueingMax(true);
        setError(null);
        try {
            const count = await view.queueMaxBuildingLevels(planet, buildingType, options, level === 0 ? plotId : undefined, queueMax.count);
            onQueuedMax?.(count);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Queue max failed');
        } finally {
            setIsQueueingMax(false);
        }
    }

    const cancelTarget = view.canManage && queuedEvents.length > 0
        ? queuedEvents.reduce((top, e) => (e.eventDetails.targetLevel > top.eventDetails.targetLevel ? e : top))
        : null;

    async function doCancel(target: TickCheckpointEventOf<'buildingBuild'>) {
        setIsCancelling(true);
        setError(null);
        try {
            await view.cancelBuildingUpgrade(planet, target.eventId, options);
            onCancelled?.();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Cancel failed');
        } finally {
            setIsCancelling(false);
        }
    }

    function requestCancel(now: number) {
        if (!cancelTarget) return;
        const target = cancelTarget;
        const isConstruct = target.eventDetails.targetLevel === 1;
        openDialog({
            title: isConstruct ? `Cancel ${def.name} construction?` : `Cancel ${def.name} level ${target.eventDetails.targetLevel}?`,
            ...cancelBuildDialogContent(planet, target, now, options.resourceMultiplier),
            confirmLabel: isConstruct ? 'Cancel construction' : 'Cancel upgrade',
            danger: true,
            onConfirm: () => { doCancel(target).catch(() => { }); },
        });
    }

    // Refund preview — 75% of the FULL (undiscounted) cost of the level
    // about to be destroyed, matching what the server actually computes.
    const destroyRefund = level >= 1 && view.allowDestroy && view.canManage
        ? calculateNominalRefundAtRates(calculateBuildingUpgradeCost(buildingType, level - 1, 0), REFUND_RATES.building, 1)
        : null;

    // Storage is capped, so a refund can partly evaporate if the planet is
    // already near capacity — warn before the player commits to it.
    const destroyOverflow = destroyRefund
        ? (['metal', 'minerals', 'gas'] as const).reduce((total, key) => {
            const amount = destroyRefund[key] ?? 0;
            if (!amount) return total;
            const { current, capacity } = planet.resources[key];
            return total + Math.max(0, current + amount - capacity);
        }, 0)
        : 0;

    async function doDestroy() {
        setIsDestroying(true);
        setDestroyError(null);
        try {
            const updated = await destroyBuilding(planet.id, buildingType, options);
            usePlanetStore.setState({ currentPlanet: updated });
            onDestroyed?.();
        } catch (err) {
            setDestroyError(err instanceof Error ? err.message : 'Destroy failed');
        } finally {
            setIsDestroying(false);
        }
    }

    function requestDestroy() {
        if (!destroyRefund) return;
        const messageParts = [`You will be refunded ${formatResourceText(destroyRefund, 'nothing')}.`];
        if (queuedEvents.length > 0) {
            messageParts.push('Any queued upgrades for this building will be cancelled first and refunded at 75%.');
        }
        if (destroyOverflow > 0) {
            messageParts.push('Warning: storage is near capacity — part of this refund will be lost.');
        }
        openDialog({
            title: `Destroy a level of ${def.name}?`,
            message: messageParts.join(' '),
            confirmLabel: 'Destroy level',
            danger: true,
            onConfirm: () => { doDestroy().catch(() => { }); },
        });
    }

    return {
        def,
        level,
        nextCost,
        atMax,
        canPrereq,
        unmetRequirements,
        affordable,
        energyShortfall,
        disabled,
        disabledReason,
        isRequesting,
        error,
        handleUpgrade,
        queueMax,
        isQueueingMax,
        handleQueueMax,
        showUnlockSlots,
        cancelTarget,
        isCancelling,
        requestCancel,
        destroyRefund,
        destroyOverflow,
        isDestroying,
        destroyError,
        requestDestroy,
    };
}
